/**
 * tax/LoanLink.tsx — 팀장님 「정책자금 대출 요청」 링크 화면 /loan/<token> (2026-10-13 지도사)
 *  링크는 하나: 팀장님이 받은 링크를 대표님께 그대로 보낸다(50~70대 · 「링크 복사하러 들어가지 않는다」)
 *   → 대표님이 적는 것이 기본 · 팀장님이 대신 적으면 위 작은 「팀장님이 적기 ›」만 누른다(대표님께 「누가 적고 계세요?」 묻지 않음 · 2026-10-13 지도사)
 *  보험사 이름은 쓰지 않는다 — 보험사와 정책자금 대출이 한 화면에 보이면 정책자금 부당영업으로 비칠 수 있다(2026-10-13 지도사) · 맨 위는 「○○○ 팀장님 소개」만
 *  키 색 = 소개한 팀장님 소속 보험사 색 · 대표님께 직접 보내는 링크는 한화생명 색(2026-10-15 지도사) — 색만(이름 · 로고 없음) · 서버가 color 로 준다
 *  칸 · 검사 · 아래 버튼은 협업방 「정책자금 대출 요청하기」(LoanRequest.tsx) 부품 그대로 · 글자는 한 단계 크게(big)
 *  2026-10-13 검수(컬러리스트 · 인지공학 · UI/UX · 토스/네이버/카카오 가이드 — 역할을 맡긴 모의 검토) 반영:
 *   빈 칸 하나하나를 세어 「남은 칸 채우러 가기 (n곳)」 → 누르면 그 칸으로 + 붉은 안내 · 주민번호는 앞 6자리 보이고 뒤 7자리만 가림 ·
 *   대표님 성함은 늘 받음(팀장님이 대신 적어도) · 완료 화면은 「닫으셔도 돼요」(처음으로 → 같은 접수 두 번 방지) · 입력 중엔 아래 버튼 숨김(휴대폰)
 *  진행 보기는 팀장님 포털(업체가 팀장님과 이어져 「정책자금 대출」 업무가 그대로 보인다) — 이 링크로는 접수만
 *  주민번호는 이 화면에 남기지 않는다 · 화면 문구에 「AI」 낱말 · 브라우저 alert/confirm · 전화 버튼 쓰지 않는다
 */
import React from 'react';
import { X, FONT, KInput, inputSt, useWide, Section } from './taxUi';
import { Choice, Field, Agree, Done, BottomCta, FilePick } from './LoanRequest';
import { digits, fmtBiz, phone, bizOk, kindOfBiz } from './taxForm';
import { shadeFor } from '../tm/portal-ui/tds';
import LoanFpView from './LoanFpView';

interface Info { fp: { name: string; title: string; has_phone: boolean } | null; direct?: boolean; kakao_url?: string; color?: string | null }   // fp 없음 = 임지도사 직접 링크 /loan/imjidosa(2026-10-14)
type Who = '' | 'ceo' | 'fp';
type Key = 'kind' | 'corp' | 'biz' | 'ceoName' | 'ceoTel' | 'rrn' | 'notify' | 'fpTel' | 'agree';
const MEMO_MAX = 500;
const mobileOk = (v: string) => /^01\d{8,9}$/.test(digits(v, 11));
const NAVY = '#1B3A5C';   // 색을 모를 때 — 고객 진행 안내 · 팀장님 포털(tds setBrand)의 대체 색과 같음(2026-10-15 디자인 자문 · 파랑은 신한 · 삼성처럼 보일 수 있음)
/** 원래 색의 HSL — 연한 바탕을 색상(色相)은 그대로 · 명도만 고정해 만든다(흰색과 섞으면 짙은 남색이 회색이 됨 · 2026-10-15 디자인 자문) */
function hslOf(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16), r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn, l = (mx + mn) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d) { h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4; h = (h * 60 + 360) % 360; }
  return [h, s, l];
}
function lightOf(hex: string, L: number): string {
  const v = hslOf(hex);
  if (!v) return X.blueLt;
  const [h] = v, s = Math.min(v[1], 0.8);
  const c = (1 - Math.abs(2 * L - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m0 = L - c / 2;
  const [a1, b1, c1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return '#' + [a1, b1, c1].map((q) => Math.round((q + m0) * 255).toString(16).padStart(2, '0')).join('').toUpperCase();
}
const ICON: Record<string, React.ReactNode> = {
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  shield: <><path d="M12 3l7 3v6c0 4.2-3 7.6-7 9-4-1.4-7-4.8-7-9V6l7-3z" /><path d="M9 12l2 2 4-4" /></>,
  people: <><circle cx="9" cy="9" r="3.2" /><path d="M3.5 19c.6-3 2.8-4.6 5.5-4.6s4.9 1.6 5.5 4.6" /><circle cx="16.5" cy="8" r="2.6" /><path d="M16 13.2c2.4 0 4 1.4 4.5 4.3" /></>,
};

export default function LoanLink({ token }: { token: string }) {
  const wide = useWide(768);
  const [info, setInfo] = React.useState<Info | null>(null);
  const [err, setErr] = React.useState('');
  // 대표님이 적는 것이 기본 · 팀장님은 위 작은 글자로 바꾼다(2026-10-13 지도사 「대표에게 누가 적고 계세요 묻는 건 어색」)
  //  팀장님께 보낸 링크(?w=fp)는 팀장님이 적기가 기본(2026-10-20 지도사 「팀장님께 보내는 링크는 디폴트가 팀장님 적기 모드여야지」) — 대표님께는 「대표님께 보내기」로 ?w 없는 링크
  const [who, setWho] = React.useState<Who>(() => { try { return new URLSearchParams(window.location.search).get('w') === 'fp' ? 'fp' : 'ceo'; } catch { return 'ceo'; } });
  // 팀장님 주소(?w=fp)로 들어왔을 때만 「보낸 대출 진행 보기」 — 대표님 주소에서 「팀장님이 적기」로 바꾼 화면엔 없다(대표님이 누르면 팀장님 휴대폰에 인증문자가 갔다 · 2026-10-28 검토)
  const [fpEntry] = React.useState<boolean>(() => who === 'fp');
  const [sent, setSent] = React.useState('');
  const [corp, setCorp] = React.useState('');
  const [biz, setBiz] = React.useState('');
  const kind = bizOk(biz) ? kindOfBiz(biz) : '';   // 개인 · 법인은 묻지 않고 사업자번호로(2026-10-14 지도사)
  const [rrnA, setRrnA] = React.useState('');
  const [rrnB, setRrnB] = React.useState('');
  const [showRrn, setShowRrn] = React.useState(false);
  const [ceoTel, setCeoTel] = React.useState('');
  const [ceoName, setCeoName] = React.useState('');
  const [fpTel, setFpTel] = React.useState('');
  const [notify, setNotify] = React.useState<'' | 'ceo' | 'referrer'>('');
  const [fin, setFin] = React.useState<File[]>([]);
  const [holders, setHolders] = React.useState<File[]>([]);
  const [memo, setMemo] = React.useState('');
  const [agree, setAgree] = React.useState(false);
  const [tried, setTried] = React.useState(false);
  const [shown, setShown] = React.useState<Key[]>([]);       // 붉은 안내를 띄운 칸 — 한꺼번에가 아니라 이동한 칸 · 지나친 칸만(혼나는 화면 방지)
  const [alertKey, setAlertKey] = React.useState<Key | ''>('');
  const [kb, setKb] = React.useState(false);                  // 휴대폰 자판이 열렸는지(커서가 아니라 화면 높이로)
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState<{ replyBy: string; merged: boolean } | null>(null);   // merged = 같은 업체 진행 중 건에 더함(2026-10-28)
  const refs = React.useRef<Partial<Record<Key, HTMLElement | null>>>({});
  const rrnARef = React.useRef<HTMLInputElement>(null);
  const rrnBRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    fetch(`/api/fp-loan/c/${encodeURIComponent(token)}`).then((r) => r.json()).then((j: { success?: boolean; data?: Info; error?: string }) => {
      if (j && j.success && j.data) setInfo(j.data); else setErr((j && j.error) || '링크를 다시 확인해 주세요');
    }).catch(() => setErr('잠시 뒤에 다시 열어 주세요'));
  }, [token]);
  React.useEffect(() => { document.title = '정책자금 대출 진단 · 임지도사'; }, []);
  // 파일을 놓는 자리 밖에 떨어뜨려도 브라우저가 그 파일을 열며 화면(적던 내용)이 사라지지 않게(2026-10-21 끌어다 놓기)
  React.useEffect(() => {
    const stop = (e: DragEvent) => { if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) e.preventDefault(); };
    window.addEventListener('dragover', stop); window.addEventListener('drop', stop);
    return () => { window.removeEventListener('dragover', stop); window.removeEventListener('drop', stop); };
  }, []);   // 「무료」는 내세우지 않는다(2026-10-14 지도사)
  // 자판이 열렸는지 — 처음 본 가장 큰 화면 높이(확대 보정)보다 25% 넘게 줄고 · 커서가 입력칸에 있을 때만
  //  (두 손가락 확대만 해도 사라지던 것 · 카톡 안 브라우저처럼 innerHeight 도 같이 줄어드는 곳 · 2026-10-13 검수)
  React.useEffect(() => {
    const vv = window.visualViewport;
    let base = 0;
    const h = () => {
      const now = vv ? vv.height * vv.scale : window.innerHeight;
      base = Math.max(base, now);
      const a = document.activeElement;
      const inField = a instanceof HTMLTextAreaElement || (a instanceof HTMLInputElement && a.type !== 'file');
      setKb(inField && now < base * 0.75);
    };
    h();
    if (vv) vv.addEventListener('resize', h);
    window.addEventListener('resize', h);
    document.addEventListener('focusin', h);
    document.addEventListener('focusout', h);
    return () => { if (vv) vv.removeEventListener('resize', h); window.removeEventListener('resize', h); document.removeEventListener('focusin', h); document.removeEventListener('focusout', h); };
  }, []);

  const fpTitle = (info && info.fp && info.fp.title) || '팀장';
  // 대표님 휴대폰 — 팀장님이 적으면 없어도 된다(2026-10-21 지도사 「휴대폰번호는 필수가 아니고」) · 결과를 대표님께 먼저 알리기로 고르면 필요 · 대표님 본인이 적으면 답변 받을 번호라 필수
  const telEmpty = !digits(ceoTel, 11);
  const telOptional = who === 'fp' && notify !== 'ceo';
  const telOk = telOptional && telEmpty ? true : mobileOk(ceoTel);
  const telMsg = who === 'fp' && telEmpty && notify === 'ceo' ? '대표님께 먼저 알리려면 휴대폰 번호가 필요해요' : '010으로 시작하는 휴대폰 번호를 적어 주세요';
  const fpName = info && info.fp ? `${info.fp.name} ${fpTitle}님` : '';
  /** 대표님께 보내기 — 대표님이 적는 링크(?w 없음) · 휴대폰 공유창(문자 · 카톡) · 안 되면 복사 */
  async function shareCeo() {
    const url = `${window.location.origin}${window.location.pathname}`;
    const text = '대표님, 정책자금 대출 검토를 위한 접수 링크입니다.\n회사 정보와 가지고 계신 서류를 올려 주시면 검토 후 연락드리겠습니다.';   // 영업 느낌 문구 안 씀(2026-10-22 지도사)
    const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string; url?: string }) => Promise<void> };
    if (typeof nav.share === 'function') {
      try { await nav.share({ text: `${text}\n\n${url}` }); return; } catch (e) { if (e instanceof Error && e.name === 'AbortError') return; }   // 글 하나로만(제목이 첫 줄에 붙고 앱마다 띄우기가 달랐다 · 2026-10-23)
    }
    try { await navigator.clipboard.writeText(`${text}\n\n${url}`); setSent('복사했어요 · 문자나 카톡에 붙여 넣으세요'); } catch { setSent(url); }
  }
  const direct = !!info && !info.fp;
  // 키 색(2026-10-15 지도사) — 소개한 팀장님 소속 보험사 색 · 직접 링크는 한화생명 색 · 모르면 임지도사 파랑 · 이름은 여전히 쓰지 않는다
  //  고객 진행 안내 · 팀장님 포털(tds setBrand)과 같은 규칙: raw = 원래 색(맨 위 띠) · fill = 흰 바탕 대비 3.0(테두리 · 점 · 아이콘 · 초점) · ink = 4.5(글자 · 흰 글자 버튼)
  //  2026-10-15 디자인 자문(역할을 맡긴 모의 검토) — 바탕이 회색(#F3F4F6)이라 글자 색은 대비 5.0 · 고르는 부품(선택 · 동의 · 버튼)은 INK 하나로 · 연한 바탕은 HSL 명도 고정
  //   RAW = 맨 위 띠만 · FILL(3.0) = 흰 바탕 위 아이콘 · 입력칸 테두리 · 완료 원 · INK(5.0) = 글자 · 고른 표시 · 아래 버튼 · SOFT(96.5%) = 혜택 · 안내 상자 · SEL(94%) = 고른 칸 바탕
  //   색을 모르거나 거의 회색(채도 0.15 미만)이면 남색 · 띠 없음 · 오류 빨강 · 「(필수)」 · 카카오 노랑은 보험사 색과 상관없이 그대로
  const hsl0 = info && info.color ? hslOf(info.color) : null;
  const raw = info && info.color && hsl0 && hsl0[1] >= 0.15 && shadeFor(info.color, 5) ? info.color.toUpperCase() : '';
  const fill = raw ? shadeFor(raw, 3) : NAVY, ink = raw ? shadeFor(raw, 5) : NAVY;
  const tone = ink, brand = fill;   // 고른 칸 테두리 · 점 · 큰 버튼 = fill(보험사 색 그대로 · 2026-10-18) · 글자 = ink
  const soft = lightOf(raw || NAVY, 0.965), toneLt = lightOf(raw || NAVY, 0.94);
  const rrn = digits(rrnA, 6) + digits(rrnB, 7);

  // 빈 칸을 하나씩 센다(묶음이 아니라 칸 — 하나 채우면 숫자가 줄어야 한다)
  const need: [Key, boolean][] = [
    ['corp', !!corp.trim()], ['biz', bizOk(biz)], ['ceoName', !!ceoName.trim()], ['ceoTel', telOk], ['rrn', rrn.length === 13],
    ...(who === 'fp' ? [['notify', !!notify] as [Key, boolean], ...(info && info.fp && !info.fp.has_phone ? [['fpTel', mobileOk(fpTel)] as [Key, boolean]] : [])] : []),
    ['agree', agree],
  ];
  const missing = need.filter(([, ok]) => !ok).map(([k]) => k);
  const ready = missing.length === 0;
  const bad = (k: Key) => shown.includes(k) && missing.includes(k);
  const started = tried || !!(kind || corp || biz || ceoName || ceoTel || rrn || agree);
  // 붉은 안내는 「한 번 적었다 비운 칸」이나 「그 아래 칸을 이미 채운 칸」만 — 살펴보고 지나가기만 해도 붉어지던 것(2026-10-13)
  const typed = React.useRef<Set<Key>>(new Set());
  ([['corp', corp], ['biz', biz], ['ceoName', ceoName], ['ceoTel', ceoTel], ['rrn', rrn], ['fpTel', fpTel]] as [Key, string][]).forEach(([k, v]) => { if (v) typed.current.add(k); });
  const touch = (k: Key) => () => {
    const order = need.map(([x]) => x);
    const laterDone = need.slice(order.indexOf(k) + 1).some(([x, ok]) => ok && x !== 'agree');
    if (missing.includes(k) && !shown.includes(k) && (typed.current.has(k) || laterDone)) setShown((x) => x.concat(k));
  };

  const set = (k: Key) => (el: HTMLElement | null) => { refs.current[k] = el; };
  const jump = (k: Key) => {
    const el = refs.current[k];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // 데려간 곳을 잠깐 강조(터치로 옮긴 초점은 테두리가 안 보여서 · 2026-10-13 검수)
    try { el.animate([{ boxShadow: `0 0 0 3px ${fill}` }, { boxShadow: `0 0 0 3px ${fill}` }, { boxShadow: '0 0 0 0 rgba(0,0,0,0)' }], { duration: 1500 }); } catch (e) { /* 애니메이션 없음 — 무시 */ }
    const i = el.matches('input,button,textarea') ? el : el.querySelector('input:not([type=file]),textarea,button');
    if (i instanceof HTMLElement) window.setTimeout(() => i.focus({ preventScroll: true }), 300);
  };
  const submit = async () => {
    if (busy) return;
    if (!ready) {
      // 아무것도 적기 전에 누르면 1/4 로 데려가기만(붉은 안내 없이) — 「신청하기」를 눌렀는데 혼나는 느낌 방지
      if (started) { setShown((x) => (x.includes(missing[0]) ? x : x.concat(missing[0]))); setAlertKey(missing[0]); }
      setTried(true); jump(missing[0]); return;
    }
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('who', who); fd.append('kind', kind); fd.append('corpName', corp.trim()); fd.append('biz', digits(biz, 10)); fd.append('rrn', rrn);
      fd.append('ceoTel', ceoTel); fd.append('ceoName', ceoName.trim());
      if (who === 'fp') { fd.append('notify', notify); if (fpTel) fd.append('reqTel', fpTel); }
      fd.append('memo', memo.trim()); fd.append('agree', '1');
      fin.forEach((f) => fd.append('fin', f));
      if (kind === '법인') holders.forEach((f) => fd.append('holders', f));
      const r = await fetch(`/api/fp-loan/c/${encodeURIComponent(token)}/requests`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => null) as { success?: boolean; data?: { reply_by: string; merged?: boolean }; error?: string } | null;
      if (!r.ok || !j || !j.success || !j.data) throw new Error((j && j.error) || '접수하지 못했어요 — 잠시 뒤 다시 시도해 주세요');
      setRrnA(''); setRrnB(''); setDone({ replyBy: j.data.reply_by, merged: !!j.data.merged });
      window.scrollTo(0, 0);
    } catch (e) { setMsg(e instanceof Error ? e.message : '접수하지 못했어요'); }
    finally { setBusy(false); }
  };
  const again = () => {
    setCorp(''); setBiz(''); setCeoTel(''); setCeoName(''); setNotify(''); setFin([]); setHolders([]); setMemo(''); setAgree(false); setTried(false); setShown([]); setAlertKey(''); setDone(null); setMsg('');
    window.scrollTo(0, 0);
  };

  const page = (body: React.ReactNode) => (
    <div className="taxroot" style={{ minHeight: '100vh', background: X.bg, fontFamily: FONT, color: X.text, wordBreak: 'keep-all', overflowWrap: 'anywhere', letterSpacing: '-0.01em', WebkitFontSmoothing: 'antialiased' }}>
      <style>{css(fill, ink)}</style>
      {raw && <div aria-hidden style={{ height: 4, background: raw }} />}   {/* 맨 위 보험사 색 띠(이름 없이 색만) */}
      {body}
    </div>
  );
  const pill = info && (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', padding: '8px 14px', borderRadius: 14, background: X.card, border: `1px solid ${X.line}`, textAlign: 'left' }}>
      <span style={{ fontSize: 16, color: X.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{direct ? '임지도사가 보내 드렸어요' : who === 'fp' ? `${fpName} 접수 링크` : `${fpName} 소개`}</span>
    </div>
  );
  const kakao = info && info.kakao_url ? (
    <a href={info.kakao_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 48, fontSize: 16, fontWeight: 500, color: X.label, textDecoration: 'underline', textUnderlineOffset: 4 }}>궁금한 점은 카카오톡 「임지도사」에 물어봐 주세요</a>
  ) : null;
  // 완료 화면 — 카카오 버튼 꼴(#FEE500 · 검정 85% · 말풍선 · 52px) · 「아래 카카오톡으로 알려 주세요」가 가리키는 곳이 보이게(2026-10-13 가이드 검수)
  const kakaoBtn = info && info.kakao_url ? (
    <a href={info.kakao_url} target="_blank" rel="noreferrer" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 52, borderRadius: 12, background: '#FEE500', color: 'rgba(0,0,0,.85)', fontSize: 17, fontWeight: 700, textDecoration: 'none' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path fill="#191919" d="M12 3C6.48 3 2 6.58 2 11c0 2.85 1.86 5.35 4.66 6.77l-.95 3.48c-.09.32.27.57.55.39l4.12-2.73c.53.06 1.07.09 1.62.09 5.52 0 10-3.58 10-8S17.52 3 12 3z" /></svg>
      카카오톡으로 물어보기
    </a>
  ) : null;
  // 붉은 안내 = 느낌표 + 옅은 빨강 띠(강조색이 주황 · 빨강이어도 색만으로 구분하지 않게) · 낭독은 이동한 첫 칸 하나만
  const errLine = (k: Key, text: string) => (bad(k) ? (
    <span role={alertKey === k ? 'alert' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#C42626', background: '#FDECEC', borderRadius: 8, padding: '8px 12px' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden style={{ flex: 'none' }}><circle cx="12" cy="12" r="11" fill="#C42626" /><path d="M12 6.5v7M12 17.2v.3" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>{text}
    </span>) : null);
  const badBorder = (k: Key): React.CSSProperties => (bad(k) ? { borderColor: '#C42626', background: '#FFF7F7' } : {});

  if (err) return page(<div style={{ maxWidth: 560, margin: '0 auto', padding: '96px 20px', textAlign: 'center', fontSize: 18, color: X.label }}>{err}</div>);
  if (!info) return page(<div style={{ padding: '96px 20px', textAlign: 'center', fontSize: 17, color: X.label }}>불러오는 중…</div>);
  if (done) {
    const [yy, mm, dd] = done.replyBy.split('-').map(Number);
    const wd = '일월화수목금토'.charAt(new Date(yy, mm - 1, dd).getDay());
    const toCeo = who === 'ceo' || notify === 'ceo';
    const rows: [string, string][] = [['답변 날짜', `${mm}월 ${dd}일(${wd})까지`], ['받는 분', toCeo ? `대표님 · ${ceoTel}` : fpName], ['받는 방법', '문자 · 카카오톡'], ['보내는 이름', '임지도사']];
    return page(
      <Done title={done.merged ? '이미 접수된 업체예요' : '대출 진단 신청을 보냈어요'} onHome={again} button={who === 'fp' ? '다른 업체도 보내기' : null} tone={fill} top={null}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 20, rowGap: 10, textAlign: 'left', background: X.card, borderRadius: 16, padding: '18px 20px', margin: '4px 0 14px' }}>
          {rows.map(([k, v]) => <React.Fragment key={k}><span style={{ color: X.label, fontSize: 16 }}>{k}</span><span style={{ minWidth: 0 }}><b style={{ color: X.text, fontSize: 17 }}>{v}</b>{k === '답변 날짜' && <span style={{ display: 'block', marginTop: 2, fontSize: 15, color: X.label, lineHeight: 1.5 }}>금융기관 사정으로 늦어지면(최대 일주일) 문자로 먼저 알려 드려요</span>}</span></React.Fragment>)}
        </div>
        <div style={{ textAlign: 'left', padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {done.merged && <span style={{ fontWeight: 700, color: X.text }}>새로 접수하지 않고, 보내신 서류와 메모는 진행 중인 접수에 더했어요.</span>}
          {toCeo && <span>번호가 틀렸으면 아래 카카오톡으로 알려 주세요.</span>}
          {!direct && <span>{who === 'fp' && fpEntry ? '진행 상황은 이 링크 맨 위 「보낸 대출 진행」에서 보실 수 있어요.' : `${fpName}도 진행 상황을 보실 수 있어요.`}</span>}
          <span>이 화면은 닫으셔도 돼요.</span>
          {kakaoBtn}
        </div>
      </Done>
    );
  }

  const big: React.CSSProperties = { height: 58, fontSize: 18 };
  return page(
    <div style={{ maxWidth: 640, margin: '0 auto', padding: wide ? '28px 20px 0' : '20px 16px 0', boxSizing: 'border-box' }}>
      {/* 혜택 머리 — 아는 팀장님이 보낸 것이 첫눈에 */}
      <div style={{ padding: '0 4px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {pill}
          {!direct && who === 'fp' && fpEntry && <LoanFpView token={token} ink={ink} role={fpTitle} />}{/* 보낸 대출 진행 — 팀장님 화면에서만 · 머리 오른쪽(2026-10-28 지도사 「숨겨도 돼」) */}
        </div>
        <h1 style={{ fontSize: wide ? 32 : 28, fontWeight: 700, lineHeight: 1.3, margin: '18px 0 0', letterSpacing: '-0.02em' }}>정책자금 대출,<br />임지도사가 알아봐 드려요</h1>
      </div>
      {/* 휴대폰은 한 줄씩(2026-10-28 지도사 「모바일에서는 영역 낭비 심하지 않나」) — 아이콘 + 제목만 · 설명 줄은 PC 에서만 */}
      <section style={{ background: soft, borderRadius: wide ? 20 : 16, padding: wide ? '16px 18px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: wide ? 14 : 8 }}>
        {([['clock', '3일 안에 1차 답변', '받을 수 있는 자금과 한도를 먼저 알려 드려요'], ['shield', '경영지도사가 직접', '기관마다 조건을 맞춰 보고 알려 드려요'], ['people', `진행은 ${fpTitle}님과 함께`, who === 'fp' && fpEntry ? '진행 단계는 맨 위 「보낸 대출 진행」에서 보실 수 있어요' : `${fpName}도 진행 단계를 보실 수 있어요`]] as const).filter(([ic]) => !(direct && ic === 'people')).map(([ic, t, d]) => (
          !wide ? (
            <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: 28 }}>
              <svg aria-hidden width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>{ICON[ic]}</svg>
              <b style={{ fontSize: 16, color: X.text, minWidth: 0 }}>{t}</b>
            </div>
          ) :
          <div key={t} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <span aria-hidden style={{ flex: 'none', width: 32, height: 32, borderRadius: 10, background: X.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={brand} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{ICON[ic]}</svg>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <b style={{ fontSize: 18 }}>{t}</b>
              <span style={{ fontSize: 16, color: X.label }}>{d}</span>
            </span>
          </div>
        ))}
      </section>

      <div className="ll-form" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 0 170px' }}>
        {/* 누가 적는지 (2026-10-27 지도사 「이거 헷갈리네」 — 「대표님이 적기 ›」 「대표님께 보내기 ›」 두 줄이 같은 모양이라 무엇이 다른지 몰랐다)
            팀장님 링크(?w=fp): 한 상자 — 「대표님이 직접 적으시게 하려면」 + 「대표님께 링크 보내기」 단추 하나(같은 폰에서 모드 바꾸기는 뺐다)
            2026-10-28 지도사 「저 버튼을 누르면 팀장님이 대신 적는 화면이 나온다고 오해」 — 상자 제목은 단추가 하는 일만 말한다 · 「팀장님이 대신 적는 칸」은 아래 업체 칸 제목 밑에
            대표님 링크: 대표님께 묻지 않는다 — 팀장님이 대신 적을 때만 아래 작은 줄을 누른다 */}
        {!direct && who === 'fp' && <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px 12px', padding: '14px 16px', borderRadius: 14, background: '#fff', border: `1px solid ${X.line}` }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: X.text }}>대표님이 직접 적으시게 하려면</div>
            <div style={{ fontSize: 15, color: X.label, marginTop: 2, wordBreak: 'keep-all' }}>{sent || '링크를 보내면 대표님 휴대폰에서 바로 적으실 수 있어요'}</div>
          </div>
          <button type="button" onClick={() => void shareCeo()} style={{ flex: wide ? '0 0 auto' : '1 1 100%', minHeight: 44, padding: '0 16px', borderRadius: 10, border: `1.5px solid ${ink}`, background: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: ink, cursor: 'pointer', whiteSpace: 'nowrap' }}>대표님께 링크 보내기</button>
        </div>}
        {!direct && who === 'ceo' && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '4px 6px', minHeight: 48 }}>
          <span style={{ fontSize: 15, color: X.label }}>{fpTitle}님이 대신 적으시나요?</span>
          <button type="button" onClick={() => setWho('fp')} style={{ border: 'none', background: 'none', minHeight: 48, padding: '0 4px', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: ink, cursor: 'pointer', whiteSpace: 'nowrap' }}>{fpTitle}님이 적기 ›</button>
        </div>}

        <Section big tone={tone} n={0} title="업체 · 대표님 정보" hint={!direct && who === 'fp' ? `${fpTitle}님이 대표님 대신 적는 칸이에요` : undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div ref={set('corp')} onBlur={touch('corp')}><Field big label="업체 이름" extra={errLine('corp', '업체 이름을 적어 주세요')}><KInput value={corp} maxLength={60} placeholder="예) 주식회사 한빛" autoComplete="organization" onChange={setCorp} style={{ ...big, ...badBorder('corp') }} /></Field></div>
            <div ref={set('biz')} onBlur={touch('biz')}><Field big label="사업자등록번호" extra={digits(biz, 10).length === 10 && !bizOk(biz) ? errBox('번호를 다시 확인해 주세요') : kind ? <span style={{ fontSize: 15, color: X.label }}>{kind === '법인' ? '법인사업자예요' : '개인사업자예요'}</span> : errLine('biz', '사업자등록번호 10자리를 적어 주세요')}>
              <KInput inputMode="numeric" value={biz} placeholder="000-00-00000" onChange={(v) => setBiz(fmtBiz(v))} style={{ ...big, ...badBorder('biz') }} />
            </Field></div>
            <div aria-hidden style={{ borderTop: `1px solid ${X.line}` }} />
            <div ref={set('ceoName')} onBlur={touch('ceoName')}><Field big label="대표님 성함" extra={errLine('ceoName', '대표님 성함을 적어 주세요')}><KInput value={ceoName} maxLength={20} placeholder="예) 홍길동" autoComplete={who === 'ceo' ? 'name' : 'off'} onChange={setCeoName} style={{ ...big, ...badBorder('ceoName') }} /></Field></div>
            <div ref={set('ceoTel')} onBlur={touch('ceoTel')}><Field big label={who === 'fp' ? '대표님 휴대폰 (없어도 돼요)' : '대표님 휴대폰'} extra={errLine('ceoTel', telMsg)}>
              <input inputMode="tel" autoComplete={who === 'ceo' ? 'tel' : 'off'} value={ceoTel} placeholder="010-0000-0000" onChange={(e) => setCeoTel(phone(e.target.value))} style={{ ...inputSt, ...big, ...badBorder('ceoTel') }} />
            </Field></div>
            <div ref={set('rrn')} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) touch('rrn')(); }} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: X.label }}>대표님 주민등록번호</span>
              <span style={{ fontSize: 15, color: X.label, marginTop: -4 }}>받을 수 있는 정책자금을 확인하는 데만 써요.</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1.25fr', gap: 8, alignItems: 'center' }}>
                <input ref={rrnARef} inputMode="numeric" autoComplete="off" aria-label="주민등록번호 앞 6자리" value={rrnA} placeholder="앞 6자리"
                  onChange={(e) => { const v = digits(e.target.value, 6); setRrnA(v); if (v.length === 6 && rrnBRef.current) rrnBRef.current.focus(); }} style={{ ...inputSt, ...big, minWidth: 0, ...(bad('rrn') && rrnA.length < 6 ? badBorder('rrn') : {}) }} />
                <span aria-hidden style={{ fontSize: 20, color: X.label }}>–</span>
                <span style={{ position: 'relative', display: 'flex', minWidth: 0 }}>
                  {/* 가림은 type=password 대신 글자 가림(css) — 아이폰 비밀번호 저장 창이 뜨지 않고 숫자 자판이 확실히 뜬다 */}
                  <input ref={rrnBRef} className={showRrn ? undefined : 'rrnmask'} type={showRrn || CAN_MASK ? 'text' : 'password'} inputMode="numeric" autoComplete="off" aria-label="주민등록번호 뒤 7자리" value={rrnB} placeholder="뒤 7자리"
                    onKeyDown={(e) => { if ((e.key === 'Backspace' || e.keyCode === 8) && !rrnB && rrnARef.current) { e.preventDefault(); rrnARef.current.focus(); } }}
                    onChange={(e) => setRrnB(digits(e.target.value, 7))} style={{ ...inputSt, ...big, flex: 1, minWidth: 0, paddingRight: 64, ...(bad('rrn') && rrnB.length < 7 ? badBorder('rrn') : {}) }} />
                  <button type="button" onClick={(e) => { e.preventDefault(); setShowRrn(!showRrn); }} aria-label={showRrn ? '뒷자리 가리기' : '뒷자리 보기'} style={{ position: 'absolute', right: 6, top: 7, height: 44, width: 52, border: 'none', borderRadius: 10, background: X.line, color: X.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>{showRrn ? '가림' : '보기'}</button>
                </span>
              </div>
              {errLine('rrn', '주민등록번호 13자리를 적어 주세요')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, borderTop: `1px solid ${X.line}`, paddingTop: 20 }}>
              <div style={{ fontSize: 16, color: X.label, background: soft, borderRadius: 12, padding: '12px 14px', lineHeight: 1.55 }}>서류는 <b style={{ color: X.text }}>있는 것만</b> 올려 주세요. 없어도 보낼 수 있어요.</div>
              <FilePick big tone={tone} toneLt={toneLt} label="직전 3개년 재무제표" files={fin} max={6} onChange={setFin} />
              {kind === '법인' && <FilePick big tone={tone} toneLt={toneLt} label="주주명부" files={holders} max={4} onChange={setHolders} />}
            </div>
          </div>
        </Section>

        <Section big tone={tone} n={0} title="동의하고 보내 주세요">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {who === 'fp' && (
              <div ref={set('notify')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: X.label }}>검토 결과는 누구에게 먼저 알려 드릴까요?</span>
                <div role="radiogroup" aria-label="결과 먼저 알릴 곳" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Choice big dotLeft tone={fill} toneLt={toneLt} on={notify === 'ceo'} onClick={() => setNotify('ceo')} title="대표님께 바로" desc="대표님 휴대폰으로 알려 드려요" />
                  <Choice big dotLeft tone={fill} toneLt={toneLt} on={notify === 'referrer'} onClick={() => setNotify('referrer')} title={`${fpTitle}님께 먼저`} desc={`${fpName}께 먼저 알려 드려요`} />
                </div>
                {errLine('notify', '누구에게 먼저 알릴지 골라 주세요')}
              </div>
            )}
            {who === 'fp' && info.fp && !info.fp.has_phone && (
              <div ref={set('fpTel')} onBlur={touch('fpTel')}><Field big label={`${fpTitle}님 휴대폰`} extra={errLine('fpTel', '010으로 시작하는 휴대폰 번호를 적어 주세요')}>
                <input inputMode="tel" autoComplete="tel" value={fpTel} placeholder="010-0000-0000" onChange={(e) => setFpTel(phone(e.target.value))} style={{ ...inputSt, ...big, ...badBorder('fpTel') }} />
              </Field></div>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 16, fontWeight: 700, color: X.label }}>임지도사에게 전할 말 <span style={{ fontWeight: 500, whiteSpace: 'nowrap', fontSize: 15 }}>(없어도 돼요) {memo.length}/{MEMO_MAX}</span></span>
              <textarea value={memo} maxLength={MEMO_MAX} rows={3} placeholder="예) 기존 대출 10억원 만기가 다가오는데, 대환대출로 금리를 낮출 수 있는지 알아봐 주세요."
                onChange={(e) => setMemo(e.target.value.slice(0, MEMO_MAX))}
                style={{ ...inputSt, height: 'auto', minHeight: 100, padding: '14px 16px', fontSize: 17, lineHeight: 1.55, resize: 'vertical' }} />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <Agree big tone={fill} title="개인정보 동의 (필수)" on={agree} onToggle={() => setAgree(!agree)} refEl={set('agree')}
                text={`정책자금 대출 진단을 위해 대표님 성함 · 주민등록번호 · 휴대폰과 올린 서류를 임지도사가 받아 써요.${direct ? '' : ` 진행 단계는 보내 주신 ${fpTitle}님도 보실 수 있어요.`} 동의하지 않으시면 진단을 도와드릴 수 없어요.`} />
              {errLine('agree', '동의를 눌러 주세요')}
            </div>
            {kakao}
          </div>
        </Section>

        {msg && <div role="alert" style={{ fontSize: 16, color: '#C42626', background: '#FDECEC', borderRadius: 12, padding: '14px 16px' }}>{msg}</div>}
      </div>

      {/* 휴대폰 자판이 열렸을 때만 숨김(커서만 남은 채 자판을 닫으면 다시 보인다) */}
      {!(kb && !wide) && (
        <BottomCta tone={fill} ready={ready} busy={busy} onClick={submit}   /* 큰 버튼 = 보험사 키 색 그대로(대비 3.0 · 18px 굵은 흰 글자) — 5.0 으로 어둡게 하면 한화 주황이 갈색으로 보였다(2026-10-18 지도사 「대출 링크도 각 보험사 색상을 키 색상으로」) */
          label={busy ? '보내는 중…' : ready || !started ? '진단 신청하기' : `남은 칸 채우러 가기 (${missing.length}곳)`} />
      )}
    </div>
  );
}

/** 뒷자리 글자 가림(css)을 못 쓰는 브라우저는 type=password 로 */
const CAN_MASK = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');
/** 붉은 안내 띠(느낌표) — 칸 검사 결과 */
function errBox(text: string) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#C42626', background: '#FDECEC', borderRadius: 8, padding: '8px 12px' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden style={{ flex: 'none' }}><circle cx="12" cy="12" r="11" fill="#C42626" /><path d="M12 6.5v7M12 17.2v.3" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>{text}
    </span>
  );
}

/** 입력칸 포커스 = 강조색 테두리 + 흰 바탕 · 키보드 초점 링 — 협업방(TaxApp)과 같은 규칙, 색만 이 화면 강조색 */
const css = (fill: string, ink: string) => [
  '@keyframes taxspin{to{transform:rotate(360deg)}}',
  `.taxroot input:not([type=checkbox]):not([type=file]):focus,.taxroot textarea:focus{border-color:${fill}!important;box-shadow:inset 0 0 0 1px ${fill};background:#fff!important}`,
  '@media (min-width:768px){.ll-form{padding-bottom:0!important}}',   // 170px 은 휴대폰 고정 버튼 자리 — PC 는 버튼이 폼 끝에 바로 붙는다(2026-10-14 지도사 「버튼이 바닥에 붙어서 안 보여」)
  '.taxroot input::placeholder,.taxroot textarea::placeholder{color:#8B95A1}',   // 예시 글자는 적은 값보다 연하게(2026-10-13 폰트 검수)
  '.taxroot span,.taxroot p,.taxroot div{text-wrap:pretty}',   // 「드려요」 한 낱말만 다음 줄로 넘어가던 것
  '.taxroot .rrnmask{-webkit-text-security:disc}',
  `.taxroot button:focus-visible,.taxroot a:focus-visible{outline:3px solid ${ink};outline-offset:2px}`,
].join('');
